// Conversation Settings Component
// Following existing patterns from ProfileSettings

import React, { useState, useCallback, useEffect } from 'react';
import { ConversationSettingsProps } from '../../../types/messaging';
import {
  SettingsContainer,
  SettingsHeader,
  SettingsContent,
  SettingsSection,
  SettingsTitle,
  SettingsOption,
  SettingsLabel,
  SettingsToggle,
  SettingsButton,
  SettingsActions,
  DangerZone,
  DangerTitle,
  DangerDescription,
  DangerButton
} from '../../../styles/Messaging/ConversationSettings.styles';

export const ConversationSettings: React.FC<ConversationSettingsProps> = ({
  conversation,
  currentUserId,
  onMuteConversation,
  onUnmuteConversation,
  onBlockUser,
  onUnblockUser,
  onLeaveConversation,
  onDeleteConversation,
  onClose,
  disabled = false
}) => {
  const [isMuted, setIsMuted] = useState(conversation.isMuted);
  const [isBlocked, setIsBlocked] = useState(conversation.isBlocked);
  const [isProcessing, setIsProcessing] = useState(false);

  // Follow the server's answer. These were seeded from the prop once, so a
  // conversation refreshed after a block elsewhere left this sheet showing the
  // old state until it was remounted.
  useEffect(() => { setIsBlocked(conversation.isBlocked); }, [conversation.isBlocked]);
  useEffect(() => { setIsMuted(conversation.isMuted); }, [conversation.isMuted]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  // Compared against the real viewer, not the string 'user-1' — a mock id left
  // over from before this screen had real data. With two participants and a
  // hardcoded id that matches neither, `find` returned participants[0]: the
  // current user in roughly half of all conversations. Blocking then aimed at
  // the wrong person, and the confirmation named them too. The database's
  // CHECK (blocker_id != blocked_id) rejected the self-block, which is why it
  // failed rather than doing something worse.
  const otherParticipant = conversation.participants.find(p => p.id !== currentUserId);

  const handleMuteToggle = useCallback(async () => {
    if (disabled || isProcessing) return;

    try {
      setIsProcessing(true);
      if (isMuted) {
        await onUnmuteConversation(conversation.id);
        setIsMuted(false);
      } else {
        await onMuteConversation(conversation.id);
        setIsMuted(true);
      }
    } catch (error) {
      console.error('Failed to toggle mute:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isMuted, conversation.id, onMuteConversation, onUnmuteConversation, disabled, isProcessing]);

  const handleBlockClick = useCallback(() => {
    if (isBlocked) {
      // If unblocking, maybe we don't need confirmation, or we can use the same flow.
      // For now, let's just proceed with toggle, but if it's BLOCKING (isBlocked is false), show confirm.
      handleConfirmBlock();
    } else {
      setShowBlockConfirm(true);
    }
  }, [isBlocked]);

  const handleConfirmBlock = useCallback(async () => {
    if (disabled || isProcessing) return;

    try {
      setIsProcessing(true);
      if (isBlocked) {
        await onUnblockUser(conversation.id, otherParticipant?.id || '');
        setIsBlocked(false);
      } else {
        await onBlockUser(conversation.id, otherParticipant?.id || '');
        setIsBlocked(true);
        setShowBlockConfirm(false); // Close confirm if open
      }
    } catch (error) {
      console.error('Failed to toggle block:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isBlocked, conversation.id, otherParticipant?.id, onBlockUser, onUnblockUser, disabled, isProcessing]);



  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (disabled || isProcessing) return;

    try {
      setIsProcessing(true);
      await onDeleteConversation(conversation.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setIsProcessing(false);
    }
  }, [conversation.id, onDeleteConversation, onClose, disabled, isProcessing]);

  if (showDeleteConfirm) {
    return (
      <SettingsContainer>
        <SettingsHeader>
          <h3>Delete Conversation?</h3>
          <button onClick={() => setShowDeleteConfirm(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
            ✕
          </button>
        </SettingsHeader>
        <SettingsContent>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Are you sure you want to delete this conversation?
            </p>
            <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
              This action cannot be undone and will permanently remove all messages.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <DangerButton
                onClick={handleConfirmDelete}
                disabled={disabled || isProcessing}
                style={{ fontSize: '1rem', padding: '10px 20px' }}
              >
                {isProcessing ? 'Deleting...' : 'Delete Conversation'}
              </DangerButton>
            </div>
          </div>
        </SettingsContent>
      </SettingsContainer>
    );
  }

  if (showBlockConfirm) {
    return (
      <SettingsContainer>
        <SettingsHeader>
          <h3>Block User?</h3>
          <button onClick={() => setShowBlockConfirm(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
            ✕
          </button>
        </SettingsHeader>
        <SettingsContent>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
            <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Are you sure you want to block {otherParticipant?.name || 'this user'}?
            </p>
            <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
              They will no longer be able to send you messages.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setShowBlockConfirm(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <SettingsButton
                onClick={handleConfirmBlock}
                disabled={disabled || isProcessing}
                $isBlocked={true} // Style as warning/blocked
                style={{ fontSize: '1rem', padding: '10px 20px' }}
              >
                {isProcessing ? 'Blocking...' : 'Block User'}
              </SettingsButton>
            </div>
          </div>
        </SettingsContent>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <SettingsHeader>
        <h3>Conversation Settings</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
          ✕
        </button>
      </SettingsHeader>

      <SettingsContent>
        <SettingsSection>
          <SettingsTitle>Notifications</SettingsTitle>

          <SettingsOption>
            <SettingsLabel>
              <div>
                <div style={{ fontWeight: 500 }}>Mute Conversation</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                  Stop receiving notifications from this conversation
                </div>
              </div>
            </SettingsLabel>
            <SettingsToggle>
              <input
                type="checkbox"
                checked={isMuted}
                onChange={handleMuteToggle}
                disabled={disabled || isProcessing}
              />
              <span className="slider"></span>
            </SettingsToggle>
          </SettingsOption>
        </SettingsSection>

        <SettingsSection>
          <SettingsTitle>Privacy</SettingsTitle>

          <SettingsOption>
            <SettingsLabel>
              <div>
                <div style={{ fontWeight: 500 }}>Block {otherParticipant?.name || 'User'}</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                  Block this user and stop receiving messages from them
                </div>
              </div>
            </SettingsLabel>
            <SettingsButton
              onClick={handleBlockClick}
              disabled={disabled || isProcessing}
              $isBlocked={isBlocked}
            >
              {isBlocked ? 'Unblock' : 'Block'}
            </SettingsButton>
          </SettingsOption>
        </SettingsSection>

        <DangerZone>
          <DangerTitle>Danger Zone</DangerTitle>

          <SettingsOption>
            <SettingsLabel>
              <div>
                <div style={{ fontWeight: 500 }}>Delete Conversation</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                  Permanently delete this conversation and all messages.
                </div>
              </div>
            </SettingsLabel>
            <DangerButton
              onClick={handleDeleteClick}
              disabled={disabled || isProcessing}
            >
              Delete
            </DangerButton>
          </SettingsOption>
        </DangerZone>
      </SettingsContent>
    </SettingsContainer>
  );
};
